import isURL from 'validator/es/lib/isURL';
import {
  translatedError, checkRequired, checkMaxLength, checkValidUrl,
} from '@/utils/errors';
import fieldNames, {
  columnDescription,
  columnLink,
  columnName,
  columnTarget,
  fieldLengths,
  globalFieldLengths,
  hasColumn,
} from '@/constants/fields';
import { vocabularyFieldLengths, vocabularyGroupings } from './constants';
import VocabularyUrlsCell from './components/VocabularyUrlsCell';
import VocabularyUrlsCard from './components/VocabularyUrlsCard';
import VocabularyLinksTable from './containers/VocabularyLinksTable';
import VocabularyEmailCell from './components/VocabularyEmailCell';
import VocabularyEmailCard from './components/VocabularyEmailCard';
import VocabularyEmailForm from './containers/VocabularyEmailForm';
import VocabularyMarkdownCell from './components/VocabularyMarkdownCell';
import VocabularyMarkdownCard from './components/VocabularyMarkdownCard';
import VocabularyMarkdownForm from './containers/VocabularyMarkdownForm';
import VocabularyRoleActionsCell from './components/VocabularyRoleActionsCell';
import VocabularyRoleActionsCard from './components/VocabularyRoleActionsCard';
import VocabularyRoleActions from './containers/VocabularyRoleActions';
import VocabularyTextCell from './components/VocabularyTextCell';
import VocabularyTextCard from './components/VocabularyTextCard';
import VocabularyTextForm from './containers/VocabularyTextForm';
import VocabularyUrlCard from './components/VocabularyUrlCard';
import VocabularyCustomLinksTable from './components/VocabularyCustomLinksTable';
import VocabularyUrlCell from './components/VocabularyUrlCell';

const validateText = ({ value }, maximum = vocabularyFieldLengths.value) => {
  const errors = [];

  checkMaxLength(value, [fieldNames.VALUE], errors, 'vocabulary', 'save.vocabulary.validation.value.title', maximum);

  return errors;
};

const validateUrl = ({ value }) => {
  const errors = validateText({ value }, globalFieldLengths.url);

  checkValidUrl(value, [fieldNames.VALUE], errors, 'vocabulary', 'save.vocabulary.validation.value.title');

  return errors;
};

const validateLink = (value, uuid, errors, field, maximum, required = true) => {
  const context = { entity: 'link', uuid };
  const fields = [field];

  checkRequired(value, fields, errors, context, null, required, `validation.${field.toLowerCase()}.required`);
  checkMaxLength(value, fields, errors, context, null, maximum);

  return { context, fields };
};

const validateUrlName = ({ name, uuid }, errors, duplicates, maximum = globalFieldLengths.name) => {
  const { context, fields } = validateLink(name, uuid, errors, fieldNames.NAME, maximum);

  if (duplicates.includes(name.toLowerCase())) {
    errors.push(translatedError(context, null, 'validation.name.not.unique', fields));
  }
};

const validateUrlHref = ({ href, uuid }, errors) => {
  const { context, fields } = validateLink(href, uuid, errors, fieldNames.URL, globalFieldLengths.url);
  checkValidUrl(href, fields, errors, context, null);
};

const validateUrlTarget = ({ target, uuid }, errors) => {
  validateLink(target, uuid, errors, fieldNames.TARGET, vocabularyFieldLengths.target, false);
};

const validateUrls = ({ urls }, columns = [columnName, columnLink, columnTarget], size = globalFieldLengths.name) => {
  const errors = [];
  const names = urls.map((item) => item.name.toLowerCase());
  const duplicates = names.filter((n, index) => names.indexOf(n) !== index);

  urls.forEach((link) => {
    if (hasColumn(columns, columnName)) {
      validateUrlName(link, errors, duplicates, size);
    }
    if (hasColumn(columns, columnLink)) {
      validateUrlHref(link, errors);
    }
    if (hasColumn(columns, columnTarget)) {
      validateLink(link.target, link.uuid, errors, fieldNames.TARGET, vocabularyFieldLengths.target, false);
    }
    if (hasColumn(columns, columnDescription)) {
      validateUrlTarget(link, errors);
    }
  });

  return errors;
};

const validateEmail = ({ value, email }) => {
  const {
    cc, bcc, subject, body,
  } = email;
  const errors = validateText({ value }, vocabularyFieldLengths.email);

  checkMaxLength(cc, [fieldNames.CC_MESSAGE], errors, 'vocabulary', null, globalFieldLengths.email);
  checkMaxLength(bcc, [fieldNames.BCC_MESSAGE], errors, 'vocabulary', null, globalFieldLengths.email);
  checkRequired(subject, [fieldNames.SUBJECT], errors, 'vocabulary', null, true, 'validation.subject.required');
  checkMaxLength(subject, [fieldNames.SUBJECT], errors, 'vocabulary', null, vocabularyFieldLengths.emailSubject);
  checkRequired(body, [fieldNames.BODY], errors, 'vocabulary', null, true, 'validation.body.required');
  checkMaxLength(body, [fieldNames.BODY], errors, 'vocabulary', null, vocabularyFieldLengths.emailBody);

  return errors;
};

const validateRoleActionHref = ({ href, uuid }, errors) => {
  const { context, fields } = validateLink(href, uuid, errors, fieldNames.URL, globalFieldLengths.url);

  if (!isURL(href, { require_tld: false, require_host: false })) {
    errors.push(translatedError(context, null, 'validation.url.not.valid', fields));
  }
};

const validateRoleActions = ({ roleActions }) => {
  const errors = [];
  const names = roleActions.map((item) => item.name.toLowerCase());
  const duplicates = names.filter((n, index) => names.indexOf(n) !== index);

  roleActions.forEach((roleAction) => {
    validateUrlName(roleAction, errors, duplicates);
    validateLink(roleAction.title, roleAction.uuid, errors, fieldNames.TITLE, globalFieldLengths.name);
    validateRoleActionHref(roleAction, errors);
    validateUrlTarget(roleAction, errors);
  });

  return errors;
};

const validateCustomLinks = (vocabulary) => (
  validateUrls(vocabulary, [columnName, columnLink, columnDescription, columnTarget], fieldLengths.micro)
);

const validateMarkdown = (vocabulary) => (validateText(vocabulary, vocabularyFieldLengths.markdown));

const vocabularyComponents = {
  [vocabularyGroupings.CUSTOM_LINKS]: {
    ValueCell: VocabularyUrlsCell,
    CardDescription: VocabularyUrlsCard,
    FormInput: VocabularyCustomLinksTable,
    validator: validateCustomLinks,
    modalSize: 'large',
    smallWizard: false,
  },
  [vocabularyGroupings.EMAIL_TEMPLATE]: {
    ValueCell: VocabularyEmailCell,
    CardDescription: VocabularyEmailCard,
    FormInput: VocabularyEmailForm,
    validator: validateEmail,
    modalSize: 'small',
    smallWizard: true,
  },
  [vocabularyGroupings.MARKDOWN]: {
    ValueCell: VocabularyMarkdownCell,
    CardDescription: VocabularyMarkdownCard,
    FormInput: VocabularyMarkdownForm,
    validator: validateMarkdown,
    smallWizard: false,
    modalSize: 'tiny',
  },
  [vocabularyGroupings.ROLE_ACTIONS]: {
    ValueCell: VocabularyRoleActionsCell,
    CardDescription: VocabularyRoleActionsCard,
    FormInput: VocabularyRoleActions,
    validator: validateRoleActions,
    modalSize: 'fullscreen',
    smallWizard: false,
  },
  [vocabularyGroupings.TEXT]: {
    ValueCell: VocabularyTextCell,
    CardDescription: VocabularyTextCard,
    FormInput: VocabularyTextForm,
    validator: validateText,
    modalSize: 'tiny',
    smallWizard: false,
  },
  [vocabularyGroupings.URL]: {
    ValueCell: VocabularyUrlCell,
    CardDescription: VocabularyUrlCard,
    FormInput: VocabularyTextForm,
    validator: validateUrl,
    modalSize: 'small',
    smallWizard: false,
  },
  [vocabularyGroupings.URLS]: {
    ValueCell: VocabularyUrlsCell,
    CardDescription: VocabularyUrlsCard,
    FormInput: VocabularyLinksTable,
    validator: validateUrls,
    modalSize: 'small',
    smallWizard: true,
  },
};

export default vocabularyComponents;
